<script setup lang="ts">
import { ref, computed } from 'vue'
import Button from 'primevue/button'
import { Icon } from '@iconify/vue'
import { removeUser } from '@/api/users'
import type { Users } from '@/types/user-types'
import { useToast } from 'primevue/usetoast'

const props = defineProps<{
  userToRemove: Users;
}>()

const emit = defineEmits<{
  (e: 'userRemoved', userId: string | number): void;
  (e: 'cancel'): void;
}>()

const loading = ref(false)
const toast = useToast()

const userFullName = computed(() => {
  if (!props.userToRemove) return 'this user'
  return `${props.userToRemove.firstName} ${props.userToRemove.lastName}`.trim()
})

const showToast = (severity: 'success' | 'error' | 'info' | 'warn', summary: string, detail: string, life: number = 3000) => {
  toast.add({ severity, summary, detail, life });
}

const handleConfirmRemove = async () => {
  if (!props.userToRemove || typeof props.userToRemove.id === 'undefined') {
    console.error('User to remove or user ID is undefined.')
    const detailMsg = 'User data is missing or invalid for removal.'
    showToast('error', 'Operation Failed', detailMsg, 5000)
    return
  }

  loading.value = true
  try {
    await removeUser(props.userToRemove.id.toString())
    showToast('success', 'User Removed', `User ${userFullName.value} has been successfully removed.`)
    emit('userRemoved', props.userToRemove.id)
  } catch (error: any) {
    console.error('Failed to remove user:', error)
    const errorMessage = error.response?.data?.message || error.message || 'Failed to remove user. Please try again.'
    showToast('error', 'Removal Error', errorMessage, 5000)
  } finally {
    loading.value = false
  }
}

const handleCancel = () => {
  emit('cancel')
}
</script>

<template>
  <div class="p-8 bg-white rounded-xl shadow-md">
    <div class="text-center mb-8">
      <Icon icon="mdi:alert-octagon-outline" class="text-6xl text-red-500 mb-5" />
      <h2 class="text-2xl font-semibold mb-3">Remove User Confirmation</h2>
      <p class="text-gray-600">
        Are you sure you want to remove <strong>{{ userFullName }}</strong>?
      </p>
    </div>

    <div class="flex justify-end gap-x-4 mt-10">
      <Button
        type="button"
        outlined
        class="px-6 py-2.5 border border-gray-300 text-gray-700 hover:bg-gray-50"
        @click="handleCancel"
        :disabled="loading"
      >
        Cancel
      </Button>

      <Button
        v-ripple
        type="button"
        class="px-6 py-2.5 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white"
        @click="handleConfirmRemove"
        :loading="loading"
      >
        <Icon v-if="loading" icon="mdi:loading" class="animate-spin text-xl mr-2" />
        <span>{{ loading ? 'Removing...' : 'Remove User' }}</span>
        <Icon v-if="!loading" icon="mdi:delete-outline" class="text-xl ml-2" />
      </Button>
    </div>
  </div>
</template>

<style scoped>
:deep(.p-button-icon-left .p-button-icon) {
  margin-right: 0.5rem;
}
:deep(.p-button-icon-right .p-button-icon) {
  margin-left: 0.5rem;
}
</style>